import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { LoginPageStyles } from './LoginPage.styles';
import { FormGroup, Label } from '../common/formInputsV2/styles/common';
import { PrimaryButton } from '../common/formInputsV2/Button.styles';
import { AuthWaves, UserIcon, LockIcon } from './AuthParts';

import { attemptLogin } from '../features/user/userActions';

import GreeniaLogo from '../assets/images/greenia-logo.webp';

const schema = yup.object({
  email: yup
    .string()
    .trim()
    .email('Inserisci un indirizzo email valido')
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'Formato email non valido',
    )
    .required('Il campo è obbligatorio'),
  password: yup
    .string()
    .required('Il campo è obbligatorio'),
});

export default function Login() {
  const navigate = useNavigate();
  const userState = useSelector((store) => store.user);
  useEffect(() => {
    if (userState.token) {
      navigate('/');
    }
  }, [userState.token]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  const submitData = ({ email, password }) => {
    attemptLogin({ email, password });
  };

  return (
    <LoginPageStyles>
      <AuthWaves />

      <form onSubmit={handleSubmit(submitData)} className="login-card" noValidate>
        <div className="login-logo">
          <img src={GreeniaLogo} width={170} height={57} alt="Greenia Logo" />
        </div>
        <h1 className="login-title">Accedi al tuo account</h1>
        <p className="login-subtitle">Inserisci le tue credenziali per continuare</p>

        {userState.error !== null && (
          <div className="login-error" role="alert">{userState.error}</div>
        )}

        <FormGroup className="login-group">
          <Label htmlFor="email">Email</Label>
          <div className="login-field">
            <UserIcon />
            <input
              {...register('email')}
              id="email"
              type="email"
              autoComplete="username"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              aria-invalid={errors.email ? 'true' : undefined}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            <div className="invalid-feedback" id="email-error">{errors.email?.message}</div>
          </div>
        </FormGroup>

        <FormGroup className="login-group">
          <Label htmlFor="password">Password</Label>
          <div className="login-field">
            <LockIcon />
            <input
              {...register('password')}
              id="password"
              type="password"
              autoComplete="current-password"
              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              aria-invalid={errors.password ? 'true' : undefined}
              aria-describedby={errors.password ? 'password-error' : undefined}
            />
            <div className="invalid-feedback" id="password-error">{errors.password?.message}</div>
          </div>
        </FormGroup>

        <div className="login-submit">
          <PrimaryButton type="submit" disabled={userState.loading}>
            {userState.loading ? 'Attendere prego...' : 'Accedi'}
          </PrimaryButton>
        </div>
        <p className="login-forgot">
          <Link to="/auth/forgot">Hai dimenticato la password?</Link>
        </p>
      </form>
    </LoginPageStyles>
  );
}
